/**
 * BannerCarousel — hero image/video strip with auto-advance and navigation dots.
 * Supports: image slideshow, banner video (with auto-retry on error), logo overlay.
 */
import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DEFAULT_BANNERS } from "./MenuConstants";

const DEFAULT_LOGO = "";

export function BannerCarousel({ banners, restaurantName, restaurantDescription, logoUrl, videoUrl, logoFormat, compact = false }) {
  const [current, setCurrent] = useState(0);
  const [videoError, setVideoError] = useState(false);
  const [logoError, setLogoError] = useState(false);
  const bannerVideoRef = useRef(null);
  const videoRetryRef = useRef(null);
  const validBanners = (banners || []).filter(url => url && url.trim().length > 0);
  const imgs = validBanners.length > 0 ? validBanners : DEFAULT_BANNERS;
  const timerRef = useRef(null);
  const logo = (logoUrl && logoUrl.trim().length > 0 && !logoError) ? logoUrl : DEFAULT_LOGO;
  const hasVideo = videoUrl && videoUrl.trim().length > 0 && !videoError;

  // Auto-retry when video fails — clears error after 8s so video reloads
  useEffect(() => {
    if (videoError && videoUrl) {
      videoRetryRef.current = setTimeout(() => setVideoError(false), 8000);
      return () => clearTimeout(videoRetryRef.current);
    }
  }, [videoError, videoUrl]);

  useEffect(() => {
    if (imgs.length <= 1 || hasVideo) return;
    timerRef.current = setInterval(() => setCurrent(p => (p + 1) % imgs.length), 5000);
    return () => clearInterval(timerRef.current);
  }, [imgs.length, hasVideo]);

  // Chrome fix: Force play banner video
  useEffect(() => {
    if (hasVideo && bannerVideoRef.current) {
      const video = bannerVideoRef.current;
      const attemptPlay = () => video.play().catch(() => {});
      video.addEventListener('loadeddata', attemptPlay);
      video.addEventListener('canplay', attemptPlay);
      if (video.readyState >= 2) attemptPlay();
      return () => {
        video.removeEventListener('loadeddata', attemptPlay);
        video.removeEventListener('canplay', attemptPlay);
      };
    }
  }, [hasVideo]);

  const go = (dir) => {
    setCurrent(p => (p + dir + imgs.length) % imgs.length);
  };

  return (
    <div
      className={`relative w-full overflow-hidden ${compact ? "rounded-xl h-[124px] sm:h-[168px] lg:h-[190px] bg-black/5" : "rounded-2xl"}`}
      style={compact ? undefined : { height: "280px" }}
      data-testid="banner-carousel"
      data-compact={compact ? "true" : "false"}
    >
      {hasVideo ? (
        <video
          ref={bannerVideoRef}
          src={videoUrl}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          onError={() => setVideoError(true)}
          data-testid="banner-video"
        />
      ) : (
        imgs.map((url, i) => (
          <div key={i} className="absolute inset-0 transition-opacity duration-700" style={{ opacity: i === current ? 1 : 0 }}>
            <img src={url} alt={`Banner ${i + 1}`} className={`w-full h-full ${compact ? "object-contain" : "object-cover"}`}
              onError={(e) => { e.target.src = DEFAULT_BANNERS[0]; }} />
          </div>
        ))
      )}
      {!compact && <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />}
      <div className={`absolute bottom-0 left-0 right-0 ${compact ? "p-2" : "p-6"} z-10`}>
        <div className="flex items-end justify-between">
          <div>
            {!compact && logo && <img src={logo} alt="Logo"
              className={`w-14 h-14 border-2 border-white/30 shadow-lg mb-3 ${logoFormat === "square" ? "rounded-lg" : "rounded-full"}`}
              onError={() => setLogoError(true)} />}
            {!compact && <h2 className="text-white text-2xl font-bold tracking-wide drop-shadow-lg">{restaurantName || ""}</h2>}
            {!compact && restaurantDescription && <p className="text-white/80 text-sm mt-1">{restaurantDescription}</p>}
          </div>
          {!hasVideo && imgs.length > 1 && (
            <div className={`flex gap-1.5 ${compact ? "mx-auto" : ""}`}>
              {imgs.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === current ? "bg-white w-5" : "bg-white/40"}`}
                  aria-label={`Show banner ${i + 1}`}
                  aria-current={i === current ? "true" : undefined}
                  data-testid={`banner-dot-${i}`} />
              ))}
            </div>
          )}
        </div>
      </div>
      {!hasVideo && imgs.length > 1 && (
        <>
          <button onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/50 transition z-10" data-testid="banner-prev"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/50 transition z-10" data-testid="banner-next"><ChevronRight className="w-5 h-5" /></button>
        </>
      )}
    </div>
  );
}
